import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles } from './MaxCharacter';
import PromptBubble from './PromptBubble';
import OptionButton from './OptionButton';
import MicrophoneDemo from './MicrophoneDemo';
import SceneCelebration from './SceneCelebration';

function speakSentence(template, option) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const text = template.replace('[option]', option);
  const utt = new SpeechSynthesisUtterance(text);
  utt.rate = 0.85;
  utt.pitch = 1.1;
  utt.volume = 1;
  window.speechSynthesis.speak(utt);
}

export default function LocationScene({ scene, sentence, onNext }) {
  const [phase, setPhase] = useState('intro');       // intro | interactive | celebration
  const [selectedOption, setSelectedOption] = useState(null);
  const [showMic, setShowMic] = useState(false);
  const [npcHighlight, setNpcHighlight] = useState(false);

  // Auto-advance from intro after 2.5s (matches walk-in duration), then speak prompt
  useEffect(() => {
    if (phase !== 'intro') return;
    const id = setTimeout(() => {
      setPhase('interactive');
      // Brief highlight pulse on the NPC
      setNpcHighlight(true);
      setTimeout(() => setNpcHighlight(false), 800);
      // Speak the question after a short delay so NPC appears first
      setTimeout(() => {
        if (!window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const utt = new SpeechSynthesisUtterance(sentence.prompt);
        utt.rate = 0.85;
        utt.pitch = 1.2;
        utt.volume = 1;
        window.speechSynthesis.speak(utt);
      }, 400);
    }, 2500);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);


  const handleOptionTap = useCallback((label) => {
    setSelectedOption(label);
    setShowMic(false);
    speakSentence(sentence.template, label);
  }, [sentence.template]);

  const handleMicToggle = useCallback(() => {
    setShowMic(prev => !prev);
  }, []);

  const handleNext = useCallback(() => {
    setPhase('celebration');
  }, []);

  const handleCelebrationDone = useCallback(() => {
    onNext();
  }, [onNext]);

  const handlePromptTap = useCallback(() => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(sentence.prompt);
    utt.rate = 0.85;
    utt.pitch = 1.2;
    utt.volume = 1;
    window.speechSynthesis.speak(utt);
  }, [sentence.prompt]);

  const micTarget = selectedOption ?? sentence.options[0].label;

  return (
    <div style={{ ...rootStyle, backgroundImage: `url(${scene.background})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      {/* Location label */}
      <div style={labelStyle}>{scene.label}</div>

      {/* Characters row — NPC left, Max right */}
      <div style={charactersRowStyle}>
        {/* NPC + speech bubble */}
        {phase === 'interactive' && (
          <div style={npcColStyle} className={npcHighlight ? 'anim-npc-highlight' : ''} onClick={handlePromptTap} role="button" aria-label="Replay question" title="Tap to hear the question again">
            <PromptBubble text={sentence.prompt} />
            <img src={scene.npc} alt="character" style={npcImgStyle} />
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Max — animated SVG during intro, static PNG once interactive */}
        <div className={`anim-${scene.animationKey}`} style={{ position: 'relative', alignSelf: 'flex-end' }}>
          <img src="/assets/max.png" alt="Max" style={maxImgStyle} />
          {phase === 'interactive' && <Sparkles />}
        </div>
      </div>

      {/* Options row at bottom */}
      {phase === 'interactive' && (
        <div style={bottomPanelStyle}>
          <div style={optionsRowStyle}>
            {sentence.options.map(opt => (
              <OptionButton
                key={opt.label}
                label={opt.label}
                emoji={opt.emoji}
                selected={selectedOption === opt.label}
                onTap={() => handleOptionTap(opt.label)}
              />
            ))}
          </div>
          <div style={bottomRowStyle}>
            <button onClick={handleMicToggle} style={micBtnStyle} aria-label="Practise speaking">
              🎤
            </button>
            <button onClick={handleNext} style={nextBtnStyle}>
              Next →
            </button>
          </div>
          {showMic && (
            <div style={{ marginTop: 8 }}>
              <MicrophoneDemo visible={true} />
              <p style={micSentenceStyle}>
                "{sentence.template.replace('[option]', micTarget)}"
              </p>
            </div>
          )}
        </div>
      )}

      {/* Celebration overlay */}
      {phase === 'celebration' && (
        <SceneCelebration onDone={handleCelebrationDone} />
      )}
    </div>
  );
}

/* ── Styles ── */
const rootStyle = {
  width: '100vw',
  height: '100vh',
  overflow: 'hidden',
  position: 'relative',
  fontFamily: "'Fredoka One', cursive",
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-end',
};

const labelStyle = {
  position: 'absolute',
  top: 20,
  left: '50%',
  transform: 'translateX(-50%)',
  background: '#FFD600',
  border: '4px solid #111',
  borderRadius: 50,
  padding: '6px 24px',
  fontFamily: "'Fredoka One', cursive",
  fontSize: '1.2rem',
  boxShadow: '4px 4px 0 #111',
};

const charactersRowStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  width: '100%',
  padding: '52px 32px 0',
  boxSizing: 'border-box',
  flex: 1,
};

const bottomPanelStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  padding: '12px 24px 16px',
  width: '100%',
  boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.6)',
  backdropFilter: 'blur(4px)',
};

const optionsRowStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: 12,
  width: '100%',
  justifyContent: 'center',
  flexWrap: 'wrap',
};

const bottomRowStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: 16,
  alignItems: 'center',
  marginTop: 8,
};

const micBtnStyle = {
  fontSize: '2rem',
  background: 'white',
  border: '4px solid #111',
  borderRadius: '50%',
  width: 60,
  height: 60,
  cursor: 'pointer',
  boxShadow: '4px 4px 0 #111',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
};

const nextBtnStyle = {
  fontFamily: "'Fredoka One', cursive",
  fontSize: '1.3rem',
  background: '#4CAF50',
  color: 'white',
  border: '4px solid #111',
  borderRadius: 50,
  padding: '12px 32px',
  cursor: 'pointer',
  boxShadow: '5px 5px 0 #111',
};

const maxImgStyle = {
  width: 160,
  height: 160,
  objectFit: 'contain',
  display: 'block',
};

const npcColStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0,
  flexShrink: 0,
  cursor: 'pointer',
  userSelect: 'none',
};

const npcImgStyle = {
  width: 160,
  height: 160,
  objectFit: 'contain',
  display: 'block',
};

const micSentenceStyle = {
  fontFamily: "'Fredoka One', cursive",
  fontSize: '1rem',
  color: '#555',
  textAlign: 'center',
  marginTop: 8,
  fontStyle: 'italic',
};
